import { getAppStore } from '../../app/AppStore';
import { ServiceLocator } from '../../app/ServiceLocator';
import { hasRunNowPermission } from '../../app/utils/MixedContentHelpers';
import { Messages } from '../../codegen/Localize';
import { ActionsMenuItem } from '../../common/react/components/SharedWidgetsActionMenu';
import { DeleteRunFlowTasksAction } from '../../flow/actions/DeleteRunFlowTasksAction';
import { showResumeRunFlowTasksActionDialog } from '../../flow/actions/ResumeRunFlowTasksAction';
import { editFlowRunTaskDialog } from '../../flow/utils/FlowDialogUtils';
import { FeatureFlags } from '../FeatureFlags';
import { RunFlowTaskInfo } from '../models/RunFlowTaskInfo';
import { Set } from '../models/Set';
import { ChangeScheduleActionService } from './ChangeScheduleAction';
import { RunFlowTasksAction } from './RunFlowTasksAction';

export module FlowTaskActions {

  export function getFlowTasksActions(
    taskSet: { readonly [id: string]: RunFlowTaskInfo },
    done: () => void
  ): ReadonlyArray<ActionsMenuItem> {
    const taskList = objectToArray(taskSet);
    const allowedActions = [];

    allowedActions.push(runFlowTasksAction(taskList, done));
    if (taskList.length > 1) { // if multiple selected we allow change schedule
      allowedActions.push(changeRunFlowTasksScheduleAction(taskList, done));
    } else { // else we just show the edit task dialog which also contains ability to change schedule
      allowedActions.push(editFlowRunTaskAction(taskList, done));
    }
    if(FeatureFlags.autoSuspendFlowTasksEnabled) {
      allowedActions.push(resumeRunFlowTasksAction(taskList, done));
    }
    allowedActions.push(deleteRunFlowTasksAction(taskList, done));

    return allowedActions;
  }

  function objectToArray<T>(obj: { readonly [id: string]: T }): ReadonlyArray<T> {
    return Object.keys(obj).map(key => obj[key]);
  }

  function runFlowTasksAction(
    tasks: ReadonlyArray<RunFlowTaskInfo>,
    done: () => void
  ): ActionsMenuItem {
    return {
      label: Messages.runNow_action(),
      execute: () => {
        const context = ServiceLocator.getAngularActionContext();
        ServiceLocator.get<RunFlowTasksAction>('RunFlowTasksAction')
          .execute(Set.fromIdItemArray(tasks), context).then(done);
      },
      disabled: !hasRunNowPermission(getAppStore().getState())
    };
  }

  function editFlowRunTaskAction(
    tasks: ReadonlyArray<RunFlowTaskInfo>,
    done: () => void
  ): ActionsMenuItem{
    return {
      label: Messages.editTask_action(),
      execute: () => {
        editFlowRunTaskDialog(tasks[0]).then(done);
      }
    };
  }

  function changeRunFlowTasksScheduleAction(
    tasks: ReadonlyArray<RunFlowTaskInfo>,
    done: () => void
  ): ActionsMenuItem {
    return {
      label: Messages.editSchedules(),
      execute: () => {
        const context = ServiceLocator.getAngularActionContext();
        ChangeScheduleActionService.forRunFlowTasks(Set.fromIdItemArray(tasks), context).then(done);
      }
    };
  }

  function deleteRunFlowTasksAction(
    tasks: ReadonlyArray<RunFlowTaskInfo>,
    done: () => void
  ): ActionsMenuItem {
    return {
      label: Messages.delete_action(),
      execute: () => {
        DeleteRunFlowTasksAction.dialog(Set.fromIdItemArray(tasks)).then(done);
      }
    };
  }

  function resumeRunFlowTasksAction(
    tasks: ReadonlyArray<RunFlowTaskInfo>,
    done: () => void
  ): ActionsMenuItem {
    return {
      label: Messages.runFlowTasksResumeAction(),
      execute: () => {
        showResumeRunFlowTasksActionDialog(Set.fromIdItemArray(tasks)).then(done);
      },
      disabled: !hasRunNowPermission(getAppStore().getState()) || (tasks.length === 1 && !tasks[0].suspended),
    };
  }

}
